"use client"

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react"

import { Button, cn, textareaContainerVariants } from "@coval/ui"
import { ArrowUp, Square } from "lucide-react"

import {
  getTemplateSelection,
  matchSlashCommands,
  type SlashCommand,
} from "../utils/slashCommands"
import { SlashCommandMenu } from "./SlashCommandMenu"

const COMPOSER_MAX_HEIGHT = 192

function resizeTextarea(element: HTMLTextAreaElement) {
  const previousHeight = element.style.height
  element.style.height = "auto"
  const fullHeight = element.scrollHeight
  // The panel mounts this composer inside `display: none` whenever it is
  // running in the background or its thread is resident-but-inactive, and an
  // element with no layout box reports `scrollHeight: 0`. Sizing to that would
  // pin the textarea at `height: 0px`, which SURVIVES the reveal: the row keeps
  // the send button's height so the composer still looks normal, but it has no
  // placeholder, no hit area, and no way to type. Leave the height alone and
  // let the next measurement — the first one taken with real layout — size it.
  if (fullHeight === 0) {
    element.style.height = previousHeight
    return
  }
  const nextHeight = Math.min(fullHeight, COMPOSER_MAX_HEIGHT)
  element.style.height = `${nextHeight}px`
  element.style.overflowY = fullHeight > COMPOSER_MAX_HEIGHT ? "auto" : "hidden"
  if (fullHeight <= COMPOSER_MAX_HEIGHT) {
    element.scrollTop = 0
  }
}

export function CoviComposer({
  isStreaming,
  lastSentTextRef,
  onCancel,
  onDraftPresenceChange,
  onSlashCommandUsed,
  onSubmit,
  value,
  onValueChange,
  disabled = false,
  clearOnSubmit = true,
  maxLength,
  allowQueue = false,
}: {
  isStreaming: boolean
  lastSentTextRef: { current: string | null }
  onCancel?: () => void
  onDraftPresenceChange: (hasDraft: boolean) => void
  onSlashCommandUsed: (command: string) => void
  onSubmit: (text: string) => void
  value?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
  clearOnSubmit?: boolean
  maxLength?: number
  allowQueue?: boolean
}) {
  const [localDraft, setLocalDraft] = useState("")
  const draft = value ?? localDraft
  const setDraft = onValueChange ?? setLocalDraft
  const [slashDismissed, setSlashDismissed] = useState(false)
  const [slashIndex, setSlashIndex] = useState(0)
  const pendingSelectionRef = useRef<{ start: number; end: number } | null>(
    null,
  )
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const slashMatches = slashDismissed ? [] : matchSlashCommands(draft)
  const activeSlashIndex = Math.min(
    slashIndex,
    Math.max(slashMatches.length - 1, 0),
  )

  useEffect(() => {
    onDraftPresenceChange(Boolean(draft.trim()))
  }, [draft, onDraftPresenceChange])

  useLayoutEffect(() => {
    const element = textareaRef.current
    if (element) {
      resizeTextarea(element)
    }
  }, [draft])

  useEffect(() => {
    const selection = pendingSelectionRef.current
    if (!selection) {
      return
    }
    pendingSelectionRef.current = null
    const element = textareaRef.current
    if (!element) {
      return
    }
    element.focus()
    element.setSelectionRange(selection.start, selection.end)
    resizeTextarea(element)
  }, [draft])

  const applySlashCommand = (command: SlashCommand) => {
    onSlashCommandUsed(command.command)
    pendingSelectionRef.current = getTemplateSelection(command.template)
    setDraft(command.template)
    setSlashIndex(0)
  }

  const submitDraft = () => {
    const trimmedDraft = draft.trim()
    if (!trimmedDraft || (isStreaming && !allowQueue) || disabled) {
      return
    }
    onSubmit(trimmedDraft)
    if (clearOnSubmit) {
      setDraft("")
    }
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
      textareaRef.current.style.overflowY = "hidden"
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashMatches.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        setSlashIndex((index) => (index + 1) % slashMatches.length)
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        setSlashIndex(
          (index) => (index - 1 + slashMatches.length) % slashMatches.length,
        )
        return
      }
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault()
        applySlashCommand(slashMatches[activeSlashIndex])
        return
      }
      if (event.key === "Tab") {
        event.preventDefault()
        const command = slashMatches[activeSlashIndex].command
        pendingSelectionRef.current = {
          start: command.length,
          end: command.length,
        }
        setDraft(command)
        setSlashIndex(0)
        return
      }
      if (event.key === "Escape") {
        event.preventDefault()
        setSlashDismissed(true)
        return
      }
    }
    if (event.key === "ArrowUp" && draft === "" && lastSentTextRef.current) {
      event.preventDefault()
      const lastSentText = lastSentTextRef.current
      pendingSelectionRef.current = {
        start: lastSentText.length,
        end: lastSentText.length,
      }
      setDraft(lastSentText)
      return
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      submitDraft()
    }
  }

  return (
    <>
      {slashMatches.length > 0 ? (
        <SlashCommandMenu
          commands={slashMatches}
          activeIndex={activeSlashIndex}
          onHighlight={setSlashIndex}
          onSelect={applySlashCommand}
        />
      ) : null}
      <div
        role="presentation"
        // Wears the shared input surface — white (dark equivalent in dark mode),
        // hairline border, standard radius — so the drawer and full-page
        // composers read as the same field as the Home landing's.
        className={cn(
          textareaContainerVariants(),
          // Extra left inset so the placeholder and caret clear the border
          // rather than sitting tight against it.
          "flex items-end gap-2 p-2 pl-3",
        )}
        onClick={(event) => {
          // Clicking the padding gutter focuses the field, but never steals
          // focus from the send/stop button sharing the row.
          if ((event.target as HTMLElement).closest("button")) {
            return
          }
          textareaRef.current?.focus()
        }}
      >
        <textarea
          ref={textareaRef}
          value={draft}
          disabled={disabled || (isStreaming && !onCancel && !allowQueue)}
          maxLength={maxLength}
          aria-label="Ask Sofia"
          onChange={(event) => {
            setDraft(event.target.value)
            setSlashDismissed(false)
            setSlashIndex(0)
            resizeTextarea(event.target)
          }}
          onKeyDown={handleKeyDown}
          placeholder="Ask Sofia…"
          rows={1}
          className="max-h-48 flex-1 resize-none self-center overflow-y-hidden bg-transparent text-sm leading-relaxed text-coval-text outline-none placeholder:text-coval-text-muted"
        />
        <Button
          icon
          type="button"
          onClick={
            isStreaming && onCancel && (!allowQueue || !draft.trim())
              ? onCancel
              : submitDraft
          }
          disabled={
            disabled ||
            (isStreaming && !allowQueue
              ? !onCancel
              : !draft.trim() && !(isStreaming && onCancel))
          }
          tooltip={
            isStreaming && allowQueue && draft.trim()
              ? "Queue message"
              : isStreaming
                ? onCancel
                  ? "Stop"
                  : "Sofia is working"
                : "Send"
          }
        >
          {isStreaming && onCancel && (!allowQueue || !draft.trim()) ? (
            <Square aria-hidden />
          ) : (
            <ArrowUp aria-hidden />
          )}
        </Button>
      </div>
    </>
  )
}
